import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { useForm, useWatch } from 'react-hook-form'

import { omitEmptyValues } from 'app/utils/helpers/form.helpers'

interface UseSearchFormParams {
  path?: string
  arrayFormat?: string[]
  customDefaultValues?: any
  defaultQueryValues?: any
}

const ignoreKeys = ['status', 'tab', 'slug']

const useSearchForm = <T extends { p?: number }>({
  path,
  arrayFormat,
  customDefaultValues,
  defaultQueryValues
}: UseSearchFormParams) => {
  const router = useRouter()

  /* This is necessary so that the form is updated only after initialization. */
  const initializedFilters = useRef<boolean>(false)

  /* This is necessary to track the click on the back button*/
  const isBackClick = useRef<boolean>(false)

  const methods = useForm<T & typeof customDefaultValues>({
    defaultValues: customDefaultValues ?? {}
  })

  const { p: page, ...values } = useWatch<T>({
    control: methods.control
  })

  const formValue = omitEmptyValues(values) as T
  const isEmptyFormValue = !Object.keys(formValue).length

  const normalizedValues = useMemo(() => {
    const routerQuery = defaultQueryValues || router.query

    const normalizedValues = Object.entries(routerQuery).reduce(
      (acc, [key, value]: [key: string, value: any]) => {
        if (arrayFormat?.includes(key) && !Array.isArray(value)) {
          acc[key] = [value]
        } else {
          if (!ignoreKeys.includes(key) && value) {
            acc[key] = value
          }
        }

        return acc
      },
      {}
    )

    const isClearForm =
      JSON.stringify(normalizedValues) === '{}' &&
      !isEmptyFormValue &&
      !isBackClick.current

    if (isClearForm) {
      methods.reset({}, { keepDefaultValues: true })
    }

    return normalizedValues
  }, [router.query])

  const { status, slug, ...routerValues } = router.query as any
  const { p: pageQuery, ...queryActiveValues } = routerValues

  const isEqualParams =
    JSON.stringify(formValue) === JSON.stringify(queryActiveValues)

  /* Update query parameters when form values change */
  useEffect(() => {
    if (initializedFilters.current && !isBackClick.current) {
      if (!isEqualParams) {
        if (!isEmptyFormValue) {
          methods.reset({ ...formValue, p: 1 })
        }

        router.push({
          pathname: path,
          query: formValue
        })

        return
      }

      if (pageQuery && isEqualParams) {
        const params = {
          ...formValue,
          p: pageQuery
        }

        methods.reset(params)

        router.push({ pathname: path, query: params }, undefined, {
          scroll: false
        })

        return
      }
    }
  }, [JSON.stringify(formValue)])

  // Update query parameter for page
  useEffect(() => {
    if (page && initializedFilters.current && !isBackClick.current) {
      router.push(
        {
          pathname: path,
          query: { ...formValue, ...(page > 1 && { p: page }) }
        },
        undefined,
        {
          scroll: false
        }
      )
    }
  }, [page])

  /* Initialize filters */
  useEffect(() => {
    if (router.isReady && !initializedFilters.current && !isBackClick.current) {
      /* Next.js makes one parameter a string and several an array, so you need to convert the parameters that should be an array */
      methods.reset(normalizedValues as any, {
        keepDefaultValues: true
      })

      initializedFilters.current = true
    }
  }, [router.isReady])

  /* Update form values when user click back button */
  useEffect(() => {
    if (isBackClick.current) {
      if (!isEqualParams) {
        const isEmptyFilters = JSON.stringify(normalizedValues) === '{}'

        methods.reset({ ...normalizedValues, ...(isEmptyFilters && { p: 1 }) })
      }

      isBackClick.current = false
    }
  }, [router?.query])

  /* Subscribe to browser history event  */
  useEffect(() => {
    window.addEventListener('popstate', () => (isBackClick.current = true))

    return () => {
      window.removeEventListener('popstate', () => (isBackClick.current = true))
    }
  }, [])

  const resetForm = useCallback(
    () => methods.reset(customDefaultValues ?? ({} as T), { keepDirty: true }),
    [methods.reset]
  )

  return {
    values: normalizedValues as T,
    methods,
    isInitialized: initializedFilters.current,
    resetForm
  }
}

export default useSearchForm
